import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HistoryIcon from "@mui/icons-material/History";
import type React from "react";
import { type ReactElement, useState } from "react";
import { forwardRef } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/Button";
import { HoverCardClickable } from "./HoverCardClickable";

export interface AIFieldStage {
  timestamp: number;
  value: any;
  preValue: any;
}

interface AlternateAIFieldValueIconProps extends React.HTMLAttributes<HTMLDivElement> {
  alternateValues: string[];
  alternateValuesLabels: string[];
  textState?: string | string[] | Record<string, any>;
  triggerJSX?: ReactElement | null;
  side?: "top" | "bottom" | "left" | "right";
  triggerClassName?: string;
  className?: string;
  sideOffset?: number;
  handleAlternativeSelect?: (value: string) => void;
  stages?: AIFieldStage[];
  lastSavedValue?: string;
}

export const AlternateAIFieldValueIcon = forwardRef<HTMLDivElement, AlternateAIFieldValueIconProps>(
  (
    {
      textState,
      side = "top",
      sideOffset = -12,
      className,
      triggerClassName,
      alternateValues,
      alternateValuesLabels,
      triggerJSX,
      handleAlternativeSelect,
      stages,
      lastSavedValue,
      ...props
    },
    ref
  ) => {
    const [showHistory, setShowHistory] = useState(false);
    const hasHistory = stages && stages.length > 1;

    // Build the display values: original 3 + Last Save (if different from others)
    const displayValues = [...alternateValues];
    const displayLabels = [...alternateValuesLabels];
    if (lastSavedValue !== undefined && lastSavedValue !== null) {
      displayValues.push(lastSavedValue);
      displayLabels.push("Last Save");
    }

    const isValueDifferent = (value: any) => {
      if (typeof textState === "string") return textState !== value;
      if (textState && !Array.isArray(textState) && typeof textState === "object") return value !== (textState as Record<string, any>).value;
      if (Array.isArray(textState))
        return !textState.some((v) => {
          if (v && typeof v === "object") return v.hasOwnProperty("value") ? (v as Record<string, any>).value !== value : false;
          if (typeof v === "string") return v !== value;
        });
      return true;
    };

    const formatTime = (ts: number) => {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    };

    const truncate = (val: any, max = 40) => {
      const s = String(val ?? "");
      return s.length > max ? s.slice(0, max) + "..." : s;
    };

    return (
      <HoverCardClickable
        side={side}
        sideOffset={sideOffset}
        className={cn("px-1", className)}
        triggerJSX={triggerJSX ?? <AutoAwesomeIcon sx={{ fontSize: 14 }} className={cn(triggerClassName)} />}
        triggerClassName={triggerClassName}
      >
        <div className={cn("flex flex-col gap-y-1", { "min-w-[280px]": showHistory })}>
          <p className="text-sm">Change the field value</p>
          <div className="flex flex-wrap gap-x-2">
            {(() => {
              const rendered = new Set<any>();
              return displayValues.map((value, index) => {
                if (value === undefined || value === null || rendered.has(value)) return null;
                rendered.add(value);
                const isDifferent = isValueDifferent(value);
                return (
                  <Button
                    key={"changeField" + index}
                    variant="link"
                    size={"tight"}
                    className={cn("px-0 py-0 pb-1 text-secondary-dark", {
                      "hover:text-purple": isDifferent,
                      "cursor-default text-darkPurple underline": !isDifferent,
                    })}
                    onClick={() => {
                      if (!isDifferent) return;
                      handleAlternativeSelect && handleAlternativeSelect(value);
                    }}
                  >
                    <span>{displayLabels[index]}</span>
                  </Button>
                );
              });
            })()}
            {hasHistory && (
              <Button
                variant="link"
                size={"tight"}
                className="px-0 py-0 pb-1 text-tertiary-dark hover:text-purple"
                onClick={() => setShowHistory(!showHistory)}
              >
                <HistoryIcon sx={{ fontSize: 14 }} />
                <span className="ml-0.5">{showHistory ? "Hide" : "History"}</span>
              </Button>
            )}
          </div>
          {showHistory && hasHistory && (
            <div className="mt-1 max-h-[200px] overflow-y-auto border-t border-gray-200 pt-1">
              <p className="mb-1 text-xs text-tertiary-dark">Change history (newest first)</p>
              {[...stages!].reverse().map((stage, i) => (
                <div key={stage.timestamp + "-" + i} className="group flex items-start gap-x-2 rounded px-1 py-0.5 text-xs hover:bg-gray-50">
                  <span className="shrink-0 text-tertiary-dark">{formatTime(stage.timestamp)}</span>
                  <span className="shrink-0 text-tertiary-dark">&rarr;</span>
                  <button
                    type="button"
                    className="truncate text-left text-secondary-dark hover:text-purple hover:underline"
                    title={String(stage.value ?? "")}
                    onClick={() => handleAlternativeSelect?.(stage.value)}
                  >
                    {truncate(stage.value)}
                  </button>
                  <span className="ml-auto shrink-0 text-tertiary-dark opacity-0 group-hover:opacity-100">was: {truncate(stage.preValue, 20)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </HoverCardClickable>
    );
  }
);

AlternateAIFieldValueIcon.displayName = "AlternateAIFieldValueIcon";
